<template>
  <AppHeader fixed>
    <SidebarToggler class="d-lg-none" display="md" mobile />
    <b-link class="navbar-brand" to="#">
      <img class="navbar-brand-full p-1"      src="img/brand/logo.png" width="100%" alt="OPTAGE">
      <img class="navbar-brand-minimized p-1" src="img/brand/logo.png" width="100%" alt="OPTAGE">
    </b-link>
    <SidebarToggler class="d-md-down-none" display="lg" :defaultOpen=true />
    <b-navbar-nav class="ml-auto">
      <DefaultHeaderDropdownAccnt :userName="user.userName" @logout-on-click="modal.logout=true" class="mr-3"/>
    </b-navbar-nav>

    <b-modal
      v-model="modal.logout"
      title="ログアウト確認"
      header-bg-variant="warning"
      hide-header-close
      no-close-on-esc
      no-close-on-backdrop
      @ok="btnLogoutOnClick"
    >
      ログアウトします。よろしいですか？
    </b-modal>
    <audio
      id="alert-sound"
      :muted="isMute"
      preload="auto">
    </audio>
  </AppHeader>
</template>
<script>
import API from '@/api'
import moment from 'moment';
import {mapGetters, mapActions} from 'vuex'
import { Header as AppHeader, SidebarToggler, AsideToggler } from '@coreui/vue'
import DefaultHeaderDropdownAccnt from './DefaultHeaderDropdownAccnt'

const BULLETIN_POLLING_INTERVAL = process.env.VUE_APP_BULLETIN_POLLING_INTERVAL
const BULLETIN_PROCESS_TYPE = 1
const AI_ALERT_SOUND = '/ai_alert.mp3'
const OBSTACLE_INSPECTION_ALERT_SOUND = '/obstacle_inspection_alert.mp3'
const PUSH_BUTTON_ALERT_SOUND = '/push_button_alert.mp3'

const DEBUG = parseInt(process.env.VUE_APP_DEBUG)
const debug = msg => {
  if (DEBUG === 1)
    console.log(msg)
}

export default {
  name: 'DefaultHeader',
  components: {
    AsideToggler,
    AppHeader,
    DefaultHeaderDropdownAccnt,
    SidebarToggler
  },
  data() {
    return {
      // ダイアログを多重に表示させない為のフラグ
      alreadyDialogDisplayed: false,
      // エラーダイアログの表示状態
      alreadyErrorDialogDisplayed: false,
      user: {
        userName: ''
      },
      modal: {
        logout: false
      },
      // 定周期タイマー(トースト表示)
      bulletinTimer: null,
      // 最新通知・発報履歴ID
      latestAlertHistoryId: null,
      // アラート音声再生中
      isPlayingSound: false,
      alertSoundSrcs: [],
    }
  },
  methods: {
    ...mapActions('auth', ['logout']),
    // 初期化処理
    async initialize() {
      // ログインユーザー名
      this.user.userName = this.displayName
      await this.createTimer()
    },
    async createTimer() {
      // タイマー削除
      clearInterval(this.bulletinTimer)
      // トースト表示データのタイマー作成
      let isInProgress = false
      this.bulletinTimer = setInterval(async () => {
        if (isInProgress) return
        isInProgress = true
        try {
          // アラート情報取得
          const data = await this.fetchBulletinAlertHistories()
          if (data == null) return  // データ取得でエラーの場合は抜ける
          if (this.latestAlertHistoryId == null) {
            // 初回のクエリ発行の場合は現在の最新のアラートIDを記録
            if (data.alertHistory.length > 0) {
              this.latestAlertHistoryId = data.alertHistory[0].alertHistoryId
            } else {
              this.latestAlertHistoryId = 0
            }
            // 処理を抜ける
            return
          }
          if (data.alertHistory.length > 0) {
            // 現在の最新のアラートIDを更新
            this.latestAlertHistoryId = data.alertHistory[data.alertHistory.length -1].alertHistoryId
            // 取得したアラート情報の通知バナーを表示
            await Promise.all(
              await data.alertHistory.map( async data => {
                await this.makeAlertHistoryToast(data)
              })
            )
            this.alertSoundPlay()
          }
        } catch (err) {
          debug(err)
        } finally {
          isInProgress = false
        }
      }, BULLETIN_POLLING_INTERVAL)
    },
    // アラート情報取得
    async fetchBulletinAlertHistories() {
      try {
        var pathStr = []
        pathStr.push('?client_id=' + this.clientId)
        pathStr.push('&process_type=' + BULLETIN_PROCESS_TYPE)
        pathStr.push('&affiliation_id=' + this.affiliationId)
        if (this.latestAlertHistoryId != null) {
          // 2回目以降のクエリ発行の場合
          pathStr.push('&alert_history_id=' + this.latestAlertHistoryId)
        }
        const params = { path: pathStr.join('') }
        const result = await API.alertHistory.get(this.idToken(), params)
        return result.data
      } catch (err) {
        // セッションタイムアウト判定
        if (err.response && err.response.status === 403) {
          return this.$router.push({path: '/session-timeout'})
        } else {
          // エラーの通知バナーを表示
          this.makeToast(
            'アラート情報（最新）の取得エラー',
            `アラート情報（最新）の取得に失敗しました。(${err.message})`,
            'warning',
            8000)
        }
        return null
      }
    },
    // 通知バナーの表示
    makeToast(title, message, variant = 'info',
      autoHideDelayMs = process.env.VUE_APP_HIDE_TOAST_DELAY) {
      this.$bvToast.toast(message, {
        title: title,
        autoHideDelay: autoHideDelayMs,
        variant: variant,
      })
    },
    async makeAlertHistoryToast(history) {
      const h = this.$createElement
      // トースト表示対象データの画像ファイルをロードして詰めておく
      // 発報画像のURLを取得
      history.occurrenceImageUrl = await this.getAlertImageFile(history.occurrenceImageUrl)
      if (history.releaseImageUrl != null) {
        // 解除画像がある場合は取得する
        history.releaseImageUrl = await this.getAlertImageFile(history.releaseImageUrl)
      }

      // トーストクリック時のモーダル作成
      const modal = await this.createModal(history, h)
      const vNodesMsg = await this.createToastMessage(h, history, modal)
      const vNodesTitle = await this.createToastHeader(h, history)
      this.$bvToast.toast([vNodesMsg], {
        autoHideDelay: process.env.VUE_APP_HIDE_TOAST_DELAY,
        title: [vNodesTitle],
        solid: true,
        variant: 'danger',
      })

      let mp3File = await this.getAlertSoundFile(history)

      // 音声ファイル登録
      this.alertSoundSrcs.push(
        {
          aac: mp3File,
        }
      )
    },
    async createModal(record, h = this.$createElement) {
      const title = await this.selectTitle(h, record)
      const message = await this.createModalMessage(record, h)
      return {title, message}
    },
    // 発報時のトーストメッセージの作成
    createToastMessage(h, history, modal) {
      return h(
        'div',
        {
          class: 'py-2 px-12p',
          on: {
            click: () => {
              this.showModal(modal)
            }
          }
        },
        [
          h('b-row', {class: ''}, [
            h('b-col', {class: ' col-12 '}, [
              h('span', {}, `${moment(history.occurrenceReceiveJst).format(this.TIME_FORMAT_JP)}`)
            ]),
            h('b-col', {class: ' col-12 '}, [
              h('span', {}, `${history.crossingName}`)
            ]),
            h('b-col', {class: ' col-12 '}, [
              h('span', {}, `${history.locationName}`)
            ]),
            h('b-col', {class: ' col-12 ml-auto'}, [
              h('b-img-lazy', {
                class: '',
                props: {
                  src: `${history.occurrenceImageUrl}`,
                  rounded: true,
                  right: true,
                  blankColor: "#99999999",
                  width: 160,
                  height: 120,
                  alt: '発生時の現場画像',
                },
              }),
            ]),
          ]),
        ]
      )
    },
    // 発報時のトーストのタイトルの作成
    createToastHeader(h, history) {
      const title = this.selectTitle(h, history)
      return h(
        'div',
        {
          class: [''],
        },
        [title]
      )
    },
    selectTitle(h, history) {
      return h('span', {class: 'text-danger'}, `${this.getAlertTypeName(history.alertType)}で危険を検知しました。`)
    },
    async showModal(modal) {
      if (this.alreadyDialogDisplayed) return true
      this.alreadyDialogDisplayed = true

      await this.$bvModal.msgBoxOk(
        [modal.message],
        {
          okTitle: '閉じる',
          hideHeaderClose: true,
          noCloseOnEsc: false,
          noCloseOnBackdrop: false,
          centered: true,
          size: 'xl',
          headerClass: 'p-0 border-bottom-0',
          bodyClass: 'pb-0',
          footerClass: 'pb-2 border-top-0',
          buttonSize: 'md',
        }
      ).catch(() => {
        this.alreadyDialogDisplayed = false
      })
      this.alreadyDialogDisplayed = false
    },
    // クリック時に表示するモーダル
    async createModalMessage(record, h) {
      return h('b-row', {class: 'no-gutters'}, [
        h('b-col', {class: ''}, [
          h('b-row', {}, [
            h('b-col', {
              class: 'cols-6 d-flex  flex-wrap',
              props: {
                sm:"4"
              }
            }, [
              h('span', {class: 'h1 align-self-end text-truncate'}, [
                h('span', {
                    class: 'h3',
                    directives: [
                      {
                        name: 'b-tooltip',
                        value: record.crossingName,
                        modifiers: {
                          left: true,
                        }
                      },
                    ],
                  },
                  `${record.crossingName}`),
              ]),
            ]),
            h('b-col', {
              class: 'cols-6 d-flex  flex-wrap',
              props: {
                sm:"4"
              }
              }, [
              h('span', {class: 'h1 align-self-end text-truncate'}, [
                h('span', {class: 'h3'},`カメラ設置場所 : ${record.locationName}`),
              ]),
            ]),
            h('b-col', {
              class: 'cols-6 d-flex  flex-wrap',
              props: {
                sm:"4"
              }
              }, [
              h('span', {class: 'h1 align-self-end text-truncate'}, [
                h('span', {class: 'h3'},`発報元 : ${this.getAlertTypeName(record.alertType)}`),
              ]),
            ]),
          ]),
          h('b-row', {class: ''}, [
            h('b-col', {class: 'cols-12'}, [
              h('span', {class: "h5 ml-auto align-self-end"}, [
                h('span', {class: 'text-muted '}, `発生日時 : ${moment(record.occurrenceReceiveJst).format(this.TIME_FORMAT_JP)} `),
              ]),
              h('b-img-lazy', {
                class: 'modal-image',
                props: {
                  src: record.occurrenceImageUrl,
                  center: true,
                  width: 800,
                  height: 600,
                  blankColor: "#99999999",
                  fluid: true,
                  alt: '発生時の現場画像',
                }
              }),
            ]),
            h('b-col', {class: 'cols-12'}, [
              h('span', {class: "h5 ml-auto align-self-end"}, [
                h(
                  'span',
                  {class: 'text-muted '},
                  `解除日時 :
                    ${
                      this.releaseDataOutput(
                        moment(record.releaseReceiveJst).format(this.TIME_FORMAT_JP),
                        record.releaseReceiveJst != null
                      )
                    }
                  `),
              ]),

              h('div',{},
                [
                  this.releaseDataOutput(
                    h('b-img-lazy', {
                      class: 'modal-image',
                      props: {
                        src: record.releaseImageUrl,
                        center: true,
                        width: 800,
                        height: 600,
                        blankColor: "#99999999",
                        fluid: true,
                        alt: '解除時の現場画像',
                      }
                    }),
                    record.releaseImageUrl != null
                  ),
                ]
              ),
            ]),
          ]),
        ])
      ])
    },
    async showErrorDialog(errorMessage) {
      this.alreadyDialogDisplayed = true
      // 既にエラーダイアログが表示されている場合は重ねて表示しない
      if (this.alreadyErrorDialogDisplayed === true) return
      this.alreadyErrorDialogDisplayed = true
      await this.$bvModal.msgBoxOk(
        `${errorMessage}`, this.errorDialogProps)
        .then(async () => {
        })
        .catch(() => {
          this.alreadyDialogDisplayed = false
          this.alreadyErrorDialogDisplayed = false
        })
      this.alreadyDialogDisplayed = false
      this.alreadyErrorDialogDisplayed = false
    },
    releaseDataOutput(contents, flag) {
      if (!flag) {
        return ''
      }
      return contents
    },
    getAlertTypeName(type) {
      if (type === 0) {
        // 異常通知
        return 'AIカメラ検知'
      } else if (type === 2) {
        // 障検装置発報
        return '障検動作'
      } else if (type === 4) {
        // 非常ボタン発報
        return '押しボタン操作'
      }
    },
    getAlertTypeSound(type) {
      if (type === 0) {
        // 異常通知
        return AI_ALERT_SOUND
      } else if (type === 2) {
        // 障検装置発報
        return OBSTACLE_INSPECTION_ALERT_SOUND
      } else if (type === 4) {
        // 非常ボタン発報
        return PUSH_BUTTON_ALERT_SOUND
      }
    },
    async btnLogoutOnClick() {
      // ログアウト処理
      this.modal.logout = false
      clearInterval(this.bulletinTimer)
      await this.logout()
      this.$router.push('/login')
    },
    isMute() {
      return !this.isPlayingSound
    },
    alertSoundElementEventListener() {
      const alertSoundElement = document.getElementById('alert-sound')
      this.alertSoundSrcs.shift()

      if (this.alertSoundSrcs.length > 0) {
        alertSoundElement.src = this.alertSoundSrcs[0].aac
        alertSoundElement.play()
      } else {
        this.isPlayingSound = false
      }
    },
    alertSoundPlay() {
      if (this.isPlayingSound) return
      this.isPlayingSound = true
      const alertSoundElement = document.getElementById('alert-sound')
      alertSoundElement.src = this.alertSoundSrcs[0].aac
      alertSoundElement.play()
    },
    // 音声ファイル取得(return URL)
    async getAlertSoundFile(param) {
      try {
        const soundUrl = '?filePath=' + param.crossingNameVoiceUrl + this.getAlertTypeSound(param.alertType)
        const downLoadFileResult = await API.fileDownLoad.downLoadFile(
          this.idToken(),
          {
            'Content-Type': 'audio/mpeg',
            path: soundUrl
          }
        )
        const blob = new Blob([downLoadFileResult], {type: "audio/mpeg"})
        return window.URL.createObjectURL(blob)
      } catch (error) {
        return null
      }
    },
    // 画像ファイル取得(return URL)
    async getAlertImageFile(imageUrl) {
      try {
        const soundUrl = '?filePath=' + imageUrl
        const downLoadFileResult = await API.fileDownLoad.downLoadFile(
          this.idToken(),
          {
            'Content-Type': 'image/jpeg',
            path: soundUrl
          }
        )
        const blob = new Blob([downLoadFileResult], {type: "image/jpeg"})
        return window.URL.createObjectURL(blob)
      } catch (error) {
        return null
      }
    },
  },
  computed: {
    ...mapGetters('auth', ['displayName','idToken','clientId','affiliationId']),

    errorDialogProps() {
      return {
        title: 'エラー',
        headerBgVariant: 'danger',
        hideHeaderClose: true,
        noCloseOnEsc: true,
        noCloseOnBackdrop: true
      }
    },
    // 成功ダイアログのプロパティ
    successDialogProps() {
      return {
        title: '成功',
        headerBgVariant: 'success',
        hideHeaderClose: true,
        noCloseOnEsc: true,
        noCloseOnBackdrop: true
      }
    },

    TIME_FORMAT_JP: () => 'YYYY/MM/DD HH:mm:ss',
  },
  async created() {
    await this.initialize()
    const alertSoundElement = document.getElementById('alert-sound')
    alertSoundElement.addEventListener('ended', this.alertSoundElementEventListener)
  },
  beforeDestroy() {
    clearInterval(this.bulletinTimer)
    const alertSoundElement = document.getElementById('alert-sound')
    alertSoundElement.removeEventListener('ended', this.alertSoundElementEventListener)
  }
}
</script>
<style lang="scss" scoped>
  .modal-image {
    width: auto;
    height: auto;
    max-height: calc(100vh - 180px);
  }

  .px-12p {
    padding-left: 12px;
    padding-right: 12px;
  }

  .test-dialog-class {
    height : 850px;
  }

</style>
